import {
  getSessionCacheIdentity,
  getPageDataCache,
} from "../shared/sessionCache.js";
import React, { useState, useRef, useEffect } from "react";
import api from "../services/api";
const PAGE_DATA_REQUIREMENTS = {
  feed: ["houses", "teachers", "activities", "students", "checkpoints"],
  rankings: [
    "houses",
    "teachers",
    "activities",
    "students",
    "checkpoints",
    "agendamentos",
  ],
  meritos: ["meritos", "activities", "students", "checkpoints", "batalhas"],
  perfil: [
    "houses",
    "teachers",
    "activities",
    "students",
    "checkpoints",
    "batalhas",
    "bonusLog",
    "redemptions",
    "transfers",
    "meritos",
  ],
  extrato: [
    "activities",
    "checkpoints",
    "bonusLog",
    "redemptions",
    "transfers",
    "batalhas",
  ],
  agendar: ["salas", "bloqueios", "agendamentos", "students"],
  cardapio: [
    "cardapio",
    "redemptions",
    "students",
    "checkpoints",
    "transfers",
    "batalhas",
  ],
  conteudos: [],
  desafios: ["batalhas", "students", "checkpoints", "activities"],
  transferir: [
    "students",
    "transfers",
    "checkpoints",
    "bonusLog",
    "redemptions",
    "batalhas",
  ],
};
const ADMIN_PAGE_DATA_REQUIREMENTS = {
  relatorio: ["houses", "teachers", "activities", "students", "checkpoints"],
  "feed-adm": ["houses", "teachers", "activities", "students", "checkpoints"],
  questoes: ["activities", "students", "checkpoints"],
  bonus: ["students", "teachers", "bonusLog"],
  "resgates-mgmt": ["cardapio", "redemptions", "students"],
  "desafios-adm": ["batalhas", "students", "checkpoints", "activities"],
  alunos: ["houses", "students", "gruposAcesso"],
  "grupos-acesso": ["gruposAcesso", "students"],
  professores: ["houses", "teachers"],
  casas: ["houses"],
  atividades: ["activities"],
  "meritos-adm": ["meritos", "activities", "students"],
  "agendamentos-adm": ["salas", "bloqueios", "agendamentos", "students"],
  "cardapio-adm": ["cardapio"],
  "conteudos-adm": [],
};
const COLLECTION_ENDPOINTS = {
  houses: "/houses",
  teachers: "/teachers",
  activities: "/activities",
  students: "/students",
  checkpoints: "/checkpoints",
  redemptions: "/redemptions",
  bonusLog: "/bonus-log",
  batalhas: "/batalhas",
  transfers: "/transfers",
  salas: "/salas",
  agendamentos: "/agendamentos",
  bloqueios: "/bloqueios",
  cardapio: "/cardapio",
  meritos: "/meritos",
  gruposAcesso: "/grupos-acesso",
};
const toArray = (response) => {
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  if (Array.isArray(response?.data?.data)) {
    return response.data.data;
  }
  return [];
};
const globalCacheRef = {
  data: {},
  invalidated: new Set(),
};

/**
 * Hook que carrega dados sob demanda (lazy-loading) para cada página
 * com sistema inteligente de invalidação de cache
 * @param {string} currentView - View/página atual
 * @param {string} adminAba - Aba do painel admin (se view === 'admin')
 * @param {boolean} enabled - Permite carregar dados apenas quando autenticado
 * @returns {object} Estado dos dados carregados
 */
export function usePageData(view, tab = null, enabled = true, house) {
  const scope = getSessionCacheIdentity(house);
  const [state, setState] = useState({
    scope,
    data: {},
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const request = useRef(0);
  useEffect(() => {
    const seq = ++request.current;
    let active = true;
    const valid = () =>
      active &&
      seq === request.current &&
      getSessionCacheIdentity(house) === scope;
    if (!enabled) {
      setState({
        scope,
        data: {},
      });
      setLoading(false);
      setError(null);
      return;
    }
    const resources = view
      ? view === "admin" && tab
        ? ADMIN_PAGE_DATA_REQUIREMENTS[tab] || []
        : PAGE_DATA_REQUIREMENTS[view] || []
      : [];
    const cache = getPageDataCache(scope);
    const pending = resources.filter(
      (name) => !cache.data[name] || cache.invalidated.has(name),
    );
    const collect = () =>
      Object.fromEntries(
        resources.map((name) => [name, cache.data[name] || []]),
      );
    setState({
      scope,
      data: collect(),
    });
    setError(null);
    if (!pending.length) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.allSettled(
      pending.map((name) => api.get(COLLECTION_ENDPOINTS[name])),
    )
      .then((results) => {
        if (!valid()) return;
        pending.forEach((name, idx) => {
          const response = results[idx];
          cache.data[name] =
            response.status === "fulfilled"
              ? Array.isArray(response.value.data)
                ? response.value.data
                : response.value.data?.data || []
              : [];
          cache.invalidated.delete(name);
        });
        setState({
          scope,
          data: collect(),
        });
        setLoading(false);
      })
      .catch(() => {
        if (valid()) {
          setError("Erro ao carregar dados");
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [view, tab, enabled, scope]);
  return {
    data: state.scope === scope ? state.data : {},
    loading,
    error,
  };
}
export function invalidateCache(collectionKey) {
  globalCacheRef.invalidated.add(collectionKey);
}
export function clearPageDataCache() {
  globalCacheRef.data = {};
  globalCacheRef.invalidated.clear();
}
export { globalCacheRef };
